import React from 'react';

import Logging from '~app/core/logging';

const log = Logging.getLogger('BundleProvider');

const ReactBundleContext = React.createContext();

function BundleContextProvider({ context, children }) {
  return (
    <ReactBundleContext.Provider value={context}>
      {children}
    </ReactBundleContext.Provider>
  );
}

function useBundle(ctx = null) {
  // XXX ideally if ctx != null we'd just use it, but we can't put useContext
  // in a conditional so we have to query it always
  const defaultContext = React.useContext(ReactBundleContext);
  return ctx ? ctx.current : defaultContext.current;
}

// const makeKey = (name, format) => `${name}::${format || 'raw'}`;

function useResources(ctx = null) {
  const bundle = useBundle(ctx);
  const ref = React.useRef();

  if (!ref.current) {
    ref.current = {
      loaded: {}, // key -> {name, format, value}
      active: new Set(), // keys
    };
  }

  // clean out anything in the loaded list that was not used
  // in the previous render pass
  for (const key of Object.keys(ref.current.loaded)) {
    if (!ref.current.active.has(key)) {
      log.trace(`releasing asset=${key}`);
      const asset = ref.current.loaded[key];
      bundle.release(asset.name, asset.format);
      delete ref.current.loaded[key];
    }
  }

  // clean out the active list to prepare for the new render pass
  ref.current.active.clear();

  // cleanup whatever assets are still alive when unmounting
  React.useEffect(
    () => () => {
      for (const [key, asset] of Object.entries(ref.current.loaded)) {
        log.trace(`releasing asset=${key}`);
        bundle.release(asset.name, asset.format);
      }
    },
    [bundle],
  );

  return React.useCallback(
    (name, format = null) => {
      const key = `${name}::${format || 'raw'}`;
      let asset = ref.current.loaded[key];
      if (!asset) {
        log.trace(`loading asset=${key}`);
        asset = {
          name,
          format,
          value: bundle.get(name, format),
        };
        ref.current.loaded[key] = asset;
      }
      ref.current.active.add(key);
      return asset.value;
    },
    [bundle],
  );
}

function useLocalizer(ctx = null) {
  const { localizer } = useBundle(ctx);
  return localizer;
}

function useT(ctx = null) {
  const { translate } = useLocalizer(ctx);
  return translate;
}

function withT(Component) {
  function WrappedComponent(props) {
    const t = useT();
    return <Component t={t} {...props} />;
  }
  return WrappedComponent;
}

function withResources(Component) {
  function WrappedComponent(props) {
    const r = useResources();
    return <Component r={r} {...props} />;
  }
  return WrappedComponent;
}

export {
  BundleContextProvider,
  useBundle,
  useResources,
  useT,
  useLocalizer,
  withT,
  withResources,
};
